const protocol = process.env.REACT_APP_PROTOCOL || "";

const normalizeBaseUrl = (url) => {
	if (!url) return "";
	return String(url).trim().replace(/\/+$/, "");
};

// Optional explicit API base URL override.
// Useful for localhost ports and iframe embeds (where `localhost` would be incorrect).
// Example: REACT_APP_API_BASE_URL=http://localhost:8000/api
const explicitApiBaseUrl = normalizeBaseUrl(process.env.REACT_APP_API_BASE_URL || "");
const questionGroupType = "group";
const questionSelectTextType = "select_text";
const questionSelectTextIconType = "select_text_icon";
const questionSelectTextImageType = "select_text_image";
const questionSelectNumberType = "select_number";
const questionSelectNumberUnitType = "select_number_unit";
const questionSelectTwoNumbersType = "select_two_numbers";
const questionSearchableDropdownType = "searchable_dropdown";
const questionFreeTextType = "freetext";
const questionDateType = "date";
const questionTimeType = "time";
const questionDiagramType = "diagram";
const questionPatientSignatureType = "patient_signature";

export const vars = {
	isProduction: (process.env.NODE_ENV || "")?.trim() === "production",
	isTesting: (process.env.NODE_ENV || "")?.trim() === "test",
	isDevelopment: (process.env.NODE_ENV || "")?.trim() === "development",
	isMaintenance: (process.env.REACT_APP_MODE || "")?.trim() === "maintenance" || false,
	app: {
		version: process.env.REACT_APP_VERSION || "",
		domain: process.env.REACT_APP_DOMAIN || "",
		adminDomain: process.env.REACT_APP_ADMIN_DOMAIN || "",
		protocol,
		// IMPORTANT: Skyflow proxy calls use `fetch` (no axios interceptors / no CRA proxy),
		// so this URL must be reachable from the browser.
		baseUrl: explicitApiBaseUrl || normalizeBaseUrl(`${protocol}://${process.env.REACT_APP_ADMIN_DOMAIN}/api`),
		name: process.env.REACT_APP_NAME || "",
		description: process.env.REACT_APP_DESCRIPTION || "",
		keywords: process.env.REACT_APP_KEYWORDS || "",
		defaultLocale: process.env.REACT_APP_LANGUAGE || "",
		liveDomain: process.env.REACT_APP_LIVE_DOMAIN || "",
		publicFolder: process.env.PUBLIC_URL || "",
		locales: ["en", "fr", "ar"],
		countryCode: "+2",
		countryName: "Egypt",
		debounceMs: 3000,
		toastsHideDelayMs: 5000,
		idleTimeOutInMinutes: process.env.REACT_APP_LOGOUT_IDLE_TIMEOUT_IN_MINUTES || 0,
		fileMaxSizeInMB: 5,
		applicationFileInputAccepts: ["application/pdf"],
		imageFileInputAccepts: ["image/jpeg", "image/png", "image/jpg"],
		/** PDF + images for HCP/patient document upload only (not order template PDF flows). */
		documentUploadMimeTypes: Array.from(
			new Set([...["application/pdf"], ...["image/jpeg", "image/png", "image/jpg", "image/webp"]])
		),
		passwordMinLength: 8,
		passwordMinLengthClinical: 12,
		passwordMaxLength: 64,
		allowReferralLetter: JSON.parse((process.env.REACT_APP_ALLOW_REFERRAL_LETTER || "false").toLowerCase()),
		allowLangSwitcher: JSON.parse((process.env.REACT_APP_ALLOW_LANG_SWITCHER || "false").toLowerCase()),
		allowParagraphView: JSON.parse((process.env.REACT_APP_ALLOW_PARAGRAPH_VIEW || "false").toLowerCase()),
		allowListView: JSON.parse((process.env.REACT_APP_ALLOW_LIST_VIEW || "false").toLowerCase()),
		allowPatientHealthCardBarcode: JSON.parse(
			(process.env.REACT_APP_ALLOW_PATIENT_HEALTH_CARD_BARCODE || "false").toLowerCase()
		),
		allowWellHealthIntegration: JSON.parse(
			(process.env.REACT_APP_WELL_HEALTH_INTEGRATION_ENABLED || "false").toLowerCase()
		),
		europeUnionCountries: [
			"austria",
			"belgium",
			"bulgaria",
			"croatia",
			"republic of cyprus",
			"czech republic",
			"denmark",
			"estonia",
			"finland",
			"france",
			"germany",
			"greece",
			"hungary",
			"ireland",
			"italy",
			"latvia",
			"lithuania",
			"luxembourg",
			"malta",
			"netherlands",
			"poland",
			"portugal",
			"romania",
			"slovakia",
			"slovenia",
			"spain",
			"sweden",
		],
		packagesCurrencies: ["CAD", "USD", "EUR", "GBP"],
		initialPackageCurrencies: "CAD",
		localSubdomain: process.env.REACT_APP_LOCAL_SUBDOMAIN_NAME || "",
		// localSubdomain: "labs",

		allowLocalHostToMultiTenantDomains: true,
		questionOutputTextException: ["Infrequent", "No specific pattern", "Permanent"],
		noteVisitType: [
			{ label: "In Person", value: "in_person" },
			{ label: "virtual", value: "virtual" },
		],
		// Virtual / In Person is hidden everywhere it used to be asked: patient
		// intake, creating an assessment, and booking an appointment. The choice
		// is hidden rather than deleted so it can be brought back from one place,
		// and while it is off no visit type is sent — recording a value nobody
		// picked would put a clinical detail on the note that was never given.
		isNoteVisitTypeShown: false,
		noteVisitRecurrence: [
			{ label: "initial", value: "initial" },
			{ label: "follow-up", value: "followup" },
		],
		// Member Types (`user.type`)
		usersTypes: {
			professional: "professional",
			officeStaff: "office_staff",
			associate: "associate",
			managementOnly: "management_only",
		},
		// where a user sits in their team, `user.position` (backend ADR 0007)
		positions: {
			accountHolder: "account_holder",
			accountManager: "account_manager",
			member: "member",
			teamModerator: "team_moderator",
		},
		allowTimeSavingCalculator: JSON.parse(
			(process.env.REACT_APP_ALLOW_TIME_SAVING_CALCULATOR || "false").toLowerCase()
		),
		questionTypes: {
			group: questionGroupType,
			selectText: questionSelectTextType,
			selectTextIcon: questionSelectTextIconType,
			selectTextImage: questionSelectTextImageType,
			selectNumber: questionSelectNumberType,
			selectNumberUnit: questionSelectNumberUnitType,
			selectTwoNumbers: questionSelectTwoNumbersType,
			searchableDropdown: questionSearchableDropdownType,
			freeText: questionFreeTextType,
			date: questionDateType,
			time: questionTimeType,
			diagram: questionDiagramType,
			patientSignature: questionPatientSignatureType,
		},
		questionSelectTypes: {
			singleSelect: "single select",
			multipleSelect: "multiple select",
		},
		definitionViewStyles: {
			tooltip: "tooltip",
			inline: "inline",
		},
		questionKeywords: {
			followUpDate: "followup-date",
			followUp: "follow-up",
			drugUnit: "drug-unit",
			vitalSigns: "vital-signs",
			copyLink: "copy-link",
			prescriptionPrint: "prescription-print",
			redFlag: "red-flag",
			groupedAnswer: "grouped-answer",
			chart: "chart",
			lineChart: "line-chart",
			temperatureChart: "temperature-chart",
			bloodPressure: "blood-pressure",
			coloredAnswers: "colored-answers",
			systolic: "systolic",
			diastolic: "diastolic",
			specialityReferral: "speciality-referral",
			medicalCondition: "medical-condition",
			duration: "duration",
			symptoms: "symptoms",
			otherRemarks: "other-remarks",
			rxOrder: "rx-order",
		},
		soapSections: {
			subjective: "subjective",
			basicSubjective: "basic_subjective",
			objective: "objective",
			history: "history",
			basicObjective: "basic_objective",
			assessments: "assessments",
			additionalAssessments: "additional_assessments",
			plan: "plan",
			final: "final",
			questions: "questions",
			orders: "orders",
			procedures: "procedures",
			medicines: "medicines",
			statements: "statements",
			groupOfMedicines: "group_of_medicines",
			educationalInfoItems: "educational_info_items",
			shortLogs: "short_logs",
		},
		questionsMeasureLayout: { list: "list", dropdown: "dropdown" },
		skyflowTables: {
			users: "patients",
			patientDoctors: "patients_doctors",
			patientOrder: "patient_order",
			patientChildOrder: "patient_child_orders",
			/** Skyflow table for pharmacy/minor-ailment case questionnaire PDF vault parity (see NoteFinal). */
			patientQuestionnaire: "patient_questionnaires",
		},
		/** File column on `patient_questionnaires` for POST /skyflow-proxy/auth/files/upload. */
		skyflowQuestionnaireFileColumn: "questionnaire_case_file",
		/**
		 * false (default): merge patient data into PDF before Skyflow (same as orders).
		 * true: upload raw S3 bytes only (smaller; may avoid 413).
		 */
		uploadQuestionnairePdfRawToSkyflow: JSON.parse(
			(process.env.REACT_APP_UPLOAD_QUESTIONNAIRE_PDF_RAW_TO_SKYFLOW ?? "false").toLowerCase()
		),
		externalSupportLink: "https://taptype.atlassian.net/servicedesk/customer/portal/2/group/-1",
		orderSubmissions: {
			statuses: {
				new: "NEW",
				scheduled: "SCHEDULED",
				rescheduled: "RESCHEDULED",
				canceled: "CANCELED",
				completed: "COMPLETED",
				followUp1Required: "FOLLOW_UP_1_REQUIRED",
				followUp2Required: "FOLLOW_UP_2_REQUIRED",
				onHold: "ON_HOLD",
				awaitingResults: "AWAITING_RESULTS",
				noShow: "NO_SHOW",
				testPerformed: "TEST_PERFORMED",
				patientNotified: "PATIENT_NOTIFIED",
				didNotBook: "DID_NOT_BOOK",
				noCommunication: "NO_COMMUNICATION",
				followUpAfterNoShow: "FOLLOW_UP_AFTER_NO_SHOW",
				testPerformedElsewhere: "TEST_PERFORMED_ELSEWHERE",
				inProgress: "IN_PROGRESS",
			},
			statusColors: {
				new: "primary-200",
				scheduled: "teal-200",
				rescheduled: "primary-300",
				canceled: "red-300",
				completed: "gray-400",
				followUp1Required: "yellow",
				follow_up_1_required: "yellow",
				followUp2Required: "yellow-200",
				follow_up_2_required: "yellow-200",
				onHold: "orange-100",
				on_hold: "orange-100",
				awaitingResults: "primary",
				awaiting_results: "primary",
				noShow: "orange-100",
				no_show: "orange-100",
				testPerformed: "success",
				test_performed: "success",
				patientNotified: "success",
				patient_notified: "success",
				did_not_book: "orange-100",
				no_communication: "orange-100",
				follow_up_after_no_show: "yellow",
				test_performed_elsewhere: "orange-100",
				inProgress: "orange-100",
				in_progress: "orange-100",
			},
		},
		tasks: {
			statuses: {
				active: "active",
				completed: "completed",
				cancelled: "cancelled",
			},
			statusColors: {
				active: "teal-200",
				completed: "primary-200",
				cancelled: "red-500",
			},
			priorities: {
				high: "high",
				normal: "normal",
				low: "low",
			},
			priorityColor: {
				high: "red-500",
				normal: "success",
				low: "orange-100",
			},
		},
	},
	social: { links: { facebook: "#", instagram: "#", twitter: "#", linkedIn: "#" } },
	appStores: { google: "#", apple: "#" },
	localStorage: {
		fieldsName: {
			token: "token",
			user: "user",
			i18nextLng: "i18nextLng",
			note: "note",
			shareNoteNotification: "shareNoteNotification",
			tempPatient: "tempPatient",
			uiUserPasswordUpdateNeeded: "uiUserPasswordUpdateNeeded",
			twoFactorAuthentication: "twoFactorAuthentication",
			uiNotesScheduleView: "uiNotesScheduleView",
			uiIsDashboardSideCollapsed: "uiIsDashboardSideCollapsed",
		},
	},
	secrets: {
		google: { recaptchaKey: process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY || "" },
		stripe: { key: process.env.REACT_APP_STRIPE_KEY || "" },
		docusign: { url: process.env.REACT_APP_DOCUSIGN_URL || "" },
		mailchimp: { url: process.env.REACT_APP_MAILCHIMP_URL || "" },
		pusher: {
			key: process.env.REACT_APP_PUSHER_KEY,
			cluster: process.env.REACT_APP_PUSHER_CLUSTER,
			host: process.env.REACT_APP_PUSHER_HOST,
			port: process.env.REACT_APP_PUSHER_PORT,
			enableTLS: JSON.parse((process.env.REACT_APP_PUSHER_ENABLE_TLS || "false").toLowerCase()),
		},
		skyflow: {
			vaultID: process.env.REACT_APP_SKYFLOW_VAULT_ID || "",
			vaultURL: process.env.REACT_APP_SKYFLOW_VAULT_URL || "",
		},
		sentry: { key: process.env.REACT_APP_SENTRY_KEY || "" },
		fullCalendar: { key: process.env.REACT_APP_FULL_CALENDAR_KEY || "" },
	},
	demo: { promo: "cHJvbW9fZnJlZXRyaWFsXzMw" },
	api: { headers: { accept: "application/json", "content-type": "application/json" } },
	pdf: {
		patientFields: {
			email: "patient-email",
			/** MedsCheck templates may use underscore; same value source as `email`. */
			emailUnderscore: "patient_email",
			name: "patient-name",
			firstName: "patient-first-name",
			lastName: "patient-last-name",
			phone: "patient-telephone-number",
			address: "patient-address",
			unitNumber: "patient-unit-number",
			streetName: "patient-street-name",
			city: "patient-city-town",
			state: "patient-province",
			zipCode: "patient-postal-code",
			signature: "patient-signature",
			dateOfBirth: "patient-dob",
			hcn: "patient-hcn",
			hcnVersion: "patient-hcn_version",
			gender: "patient-gender",
			pharmacistSignature: "pharmacists-signature",
			rxMedications: "rx-medications",
		},
	},
	justmeds: { tenantName: "justmeds" },
	patients: { tenantName: "patients" },
	documents: {
		types: [
			{ label: "RX", value: "RX" },
			{ label: "Educational Info", value: "EDU_INFO" },
			{ label: "Referrals", value: "REFERRALS" },
			{ label: "Lab results", value: "LAB_RESULTS" },
			{ label: "Diagnostic test", value: "DIAGNOSTIC_TEST" },
			{ label: "Diagnostic imaging", value: "DIAGNOSTIC_IMAGING" },
			{ label: "Other", value: "OTHER" },
		],
	},
	fullCalendar: {
		views: {
			dayGridMonth: "dayGridMonth",
			timeGridWeek: "timeGridWeek",
			timeGridDay: "timeGridDay",
			resourceTimeGridDay: "resourceTimeGridDay",
		},
	},
};

export default vars;
